import React from 'react';
import { Store, Globe, Zap, ArrowRight, DollarSign, FileText, BarChart, Users } from 'lucide-react';
import { ChevronRight, Clock, Truck, Shield } from 'lucide-react';

interface SellersPageProps {
  setCurrentPage: (page: string) => void;
}

const SellersPage = ({ setCurrentPage }: SellersPageProps) => (
  <div>
    <div className="relative bg-gradient-to-r from-blue-600 to-blue-800">
      <div className="max-w-7xl mx-auto py-24 px-4">
        <div className="text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
            For Sellers
            <span className="block text-blue-200">Expand Your Market Reach</span>
          </h1>
          <p className="mt-6 max-w-2xl mx-auto text-xl text-blue-100">
            Connect with thousands of verified buyers and streamline your distribution process with our comprehensive platform.
          </p>
        </div>
      </div>
    </div>

    <div className="max-w-7xl mx-auto py-16 px-4">
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Reach More Customers</h2>
          <p className="mt-4 text-lg text-gray-600">
            Join our network of successful beverage suppliers and access a growing customer base of restaurants, bars, and retailers.
          </p>
          <ul className="mt-8 space-y-4">
            <li className="flex items-start">
              <div className="flex-shrink-0">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <p className="ml-3 text-base text-gray-700">
                Connect with thousands of verified buyers across the country
              </p>
            </li>
            <li className="flex items-start">
              <div className="flex-shrink-0">
                <Globe className="h-6 w-6 text-blue-600" />
              </div>
              <p className="ml-3 text-base text-gray-700">
                Expand your market reach without additional marketing costs
              </p>
            </li>
            <li className="flex items-start">
              <div className="flex-shrink-0">
                <Zap className="h-6 w-6 text-blue-600" />
              </div>
              <p className="ml-3 text-base text-gray-700">
                Fast onboarding process to start selling immediately
              </p>
            </li>
          </ul>
        </div>
        <div className="bg-gray-50 p-8 rounded-lg">
          <h3 className="text-xl font-semibold text-gray-900">Seller Success Metrics</h3>
          <div className="mt-6 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Average Order Value</span>
              <span className="text-blue-600 font-medium">$2,500+</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Customer Retention</span>
              <span className="text-blue-600 font-medium">85%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Platform Fee</span>
              <span className="text-blue-600 font-medium">3%</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className="bg-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-gray-900">Powerful Tools for Sellers</h2>
          <p className="mt-4 text-xl text-gray-600">Everything you need to manage and grow your business</p>
        </div>
        
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="flex justify-center">
              <Store className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="mt-4 text-lg font-medium text-gray-900">Digital Storefront</h3>
            <p className="mt-2 text-sm text-gray-500">Customizable product listings with real-time inventory</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="flex justify-center">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="mt-4 text-lg font-medium text-gray-900">Order Management</h3>
            <p className="mt-2 text-sm text-gray-500">Streamlined order processing and fulfillment</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="flex justify-center">
              <BarChart className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="mt-4 text-lg font-medium text-gray-900">Analytics Dashboard</h3>
            <p className="mt-2 text-sm text-gray-500">Detailed insights into sales and customer behavior</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="flex justify-center">
              <DollarSign className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="mt-4 text-lg font-medium text-gray-900">Payment Processing</h3>
            <p className="mt-2 text-sm text-gray-500">Secure and efficient payment collection</p>
          </div>
        </div>
      </div>
    </div>

    <div className="max-w-7xl mx-auto py-16 px-4">
      <div className="bg-blue-600 rounded-lg shadow-xl overflow-hidden">
        <div className="px-6 py-8 sm:p-10">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-white">Ready to Grow Your Business?</h2>
            <p className="mt-4 text-xl text-blue-100">
              Join our network of successful suppliers and start reaching more customers today
            </p>
            <div className="mt-8">
              <button onClick={() => setCurrentPage('contact')}
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-blue-600 bg-white hover:bg-blue-50">
                Start Selling Today
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default SellersPage; 